// components/LocationSelectorModal.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch, FiMapPin, FiMap, FiGlobe, FiNavigation } from 'react-icons/fi';
import { GetCountries, GetState, GetCity } from 'react-country-state-city';
import '../styles/LocationSelectorModal.scss';

const LocationSelectorModal = ({
  visible,
  onClose,
  onSelect,
  type = 'city', // 'country', 'state', or 'city'
  countryId = null,
  stateId = null,
  currentValue = null,
  onNavigateToState = null, // Callback to switch to state view
  onNavigateToCountry = null, // Callback to switch to country view
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);

  useEffect(() => {
    if (visible) {
      loadOptions();
    }
  }, [visible, type, countryId, stateId]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        option.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchQuery, options]);

  const loadOptions = async () => {
    setLoading(true);
    try {
      let data = [];
      
      if (type === 'country') {
        console.log('🌍 Loading countries...');
        data = await GetCountries();
        console.log(`✅ Loaded ${data.length} countries`);
      } else if (type === 'state') {
        if (!countryId) {
          console.error('❌ No countryId provided for state selection');
          console.log('Current props:', { type, countryId, stateId });
          setOptions([]);
          setFilteredOptions([]);
          setLoading(false);
          return;
        }
        console.log(`🗺️ Loading states for countryId: ${countryId}`);
        data = await GetState(countryId);
        console.log(`✅ Loaded ${data.length} states`);
      } else if (type === 'city') {
        if (!countryId || !stateId) {
          console.error('❌ Missing IDs for city selection');
          console.log('Current props:', { type, countryId, stateId });
          setOptions([]);
          setFilteredOptions([]);
          setLoading(false);
          return;
        }
        console.log(`🏙️ Loading cities for countryId: ${countryId}, stateId: ${stateId}`);
        data = await GetCity(countryId, stateId);
        console.log(`✅ Loaded ${data.length} cities`);
      }

      setOptions(data || []);
      setFilteredOptions(data || []);
    } catch (error) {
      console.error(`❌ Error loading ${type}:`, error);
      setOptions([]);
      setFilteredOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchCurrentLocation = async () => {
    setFetchingLocation(true);

    try {
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by your browser');
        setFetchingLocation(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Use reverse geocoding to get location
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            
            if (data && data.address) {
              const city = data.address.city || data.address.town || data.address.village;
              const state = data.address.state;
              const country = data.address.country;
              
              if (city && state && country) {
                // Get location IDs
                const countries = await GetCountries();
                const countryData = countries.find(c => c.name === country);
                
                if (!countryData) {
                  alert('Could not find country data');
                  setFetchingLocation(false);
                  return;
                }

                const states = await GetState(countryData.id);
                const stateData = states.find(s => s.name === state);
                
                if (!stateData) {
                  alert('Could not find state data');
                  setFetchingLocation(false);
                  return;
                }

                const cities = await GetCity(countryData.id, stateData.id);
                const cityData = cities.find(c => c.name === city);
                
                if (cityData) {
                  onSelect({
                    city: cityData.name,
                    cityId: cityData.id,
                    state: stateData.name,
                    stateId: stateData.id,
                    country: countryData.name,
                    countryId: countryData.id,
                  });
                  onClose();
                } else {
                  alert('Could not find city in our database');
                }
              } else {
                alert('Could not determine location details');
              }
            } else {
              alert('Could not get location details');
            }
          } catch (error) {
            console.error('Error fetching location:', error);
            alert('Failed to get location details');
          } finally {
            setFetchingLocation(false);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Failed to get your location. Please enable location access.');
          setFetchingLocation(false);
        }
      );
    } catch (error) {
      console.error('Error fetching current location:', error);
      alert('Failed to get current location');
      setFetchingLocation(false);
    }
  };

  const handleSelect = (item) => {
    if (type === 'country') {
      onSelect({
        country: item.name,
        countryId: item.id,
      });
    } else if (type === 'state') {
      onSelect({
        state: item.name,
        stateId: item.id,
      });
    } else if (type === 'city') {
      onSelect({
        city: item.name,
        cityId: item.id,
      });
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  const getTitle = () => {
    switch (type) {
      case 'country':
        return 'Select Country';
      case 'state':
        return `Select State`;
      case 'city':
        return `Select City`;
      default:
        return 'Select Location';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'country':
        return <FiGlobe size={20} />;
      case 'state':
        return <FiMap size={20} />;
      case 'city':
        return <FiMapPin size={20} />;
      default:
        return <FiMapPin size={20} />;
    }
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <div className="location-modal-overlay" onClick={handleClose}>
        <motion.div
          className="location-modal"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="location-modal-header">
            <div className="header-top">
              <div className="header-title">
                {getIcon()}
                <h3>{getTitle()}</h3>
              </div>
              <button className="close-btn" onClick={handleClose}>
                <FiX />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="location-search">
            <FiSearch />
            <input
              type="text"
              placeholder={`Search ${type}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')}>
                <FiX />
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {/* Current Location Button - only show for city view */}
            {type === 'city' && (
              <button 
                className="action-btn current-location-btn" 
                onClick={handleFetchCurrentLocation}
                disabled={fetchingLocation}
              >
                <FiNavigation size={18} />
                <span>{fetchingLocation ? 'Getting Location...' : 'Use Current Location'}</span>
              </button>
            )}

            {/* Navigate to State button (shown when in city view) */}
            {type === 'city' && onNavigateToState && (
              <button className="action-btn" onClick={onNavigateToState}>
                <FiMap size={18} />
                <span>Change State</span>
              </button>
            )}

            {/* Navigate to Country button (shown when in state view) */}
            {type === 'state' && onNavigateToCountry && (
              <button className="action-btn" onClick={onNavigateToCountry}>
                <FiGlobe size={18} />
                <span>Change Country</span>
              </button>
            )}
          </div>

          {/* Options List */}
          <div className="location-options">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading {type}s...</p>
              </div>
            ) : filteredOptions.length > 0 ? (
              <div className="options-list">
                {filteredOptions.map((item) => (
                  <button
                    key={item.id}
                    className={`option-item ${
                      currentValue === item.name ? 'selected' : ''
                    }`}
                    onClick={() => handleSelect(item)}
                  >
                    <div className="option-content">
                      {getIcon()}
                      <span>{item.name}</span>
                    </div>
                    {currentValue === item.name && (
                      <div className="checkmark">✓</div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <FiSearch size={48} />
                <h4>No results found</h4>
                <p>
                  {searchQuery
                    ? `No ${type}s match "${searchQuery}"`
                    : !countryId && type !== 'country'
                      ? 'Please select a country first'
                      : !stateId && type === 'city'
                        ? 'Please select a state first'
                        : `No ${type}s available`}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LocationSelectorModal;